import { useState, useMemo, useEffect, useRef } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListDestinationPacketTemplates,
  useCreateDestinationEnvelopeInstance,
  CreateEnvelopeInstanceValidation,
  type DestinationPacketDocument,
} from "@/lib/api/destinationPackets";
import { useListCustomers } from "@/lib/api/customer";
import { Customer } from "@/lib/api/reports";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { Loader2, Send } from "lucide-react";

interface DocumentOverrideFields {
  customer_name?: string;
  customer_email?: string;
}

interface SelectedDocument {
  id: string;
  override_fields?: DocumentOverrideFields;
}

interface FormData {
  customer_id: string;
  packet_id: string | null;
  selected_documents: SelectedDocument[];
  is_test_send: boolean;
}

interface SendDestinationEnvelopeFormCoreProps {
  initialCustomerId?: string;
  initialPacketId?: string | null;
  onSuccess?: () => void;
  onCancel?: () => void;
  isModal?: boolean;
}

export function SendDestinationEnvelopeFormCore({
  initialCustomerId,
  initialPacketId,
  onSuccess,
  onCancel,
  isModal = false,
}: SendDestinationEnvelopeFormCoreProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createEnvelopeInstance = useCreateDestinationEnvelopeInstance();
  const templatesResult = useListDestinationPacketTemplates();
  const customersResult = useListCustomers({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    [documentId: string]: {
      customer_email?: string;
    };
  }>({});
  const hasAutoSelectedRef = useRef<string | null>(null);

  const [formData, setFormData] = useState<FormData>(() => ({
    customer_id: initialCustomerId || "",
    packet_id: initialPacketId || null,
    selected_documents: [],
    is_test_send: false,
  }));

  // Get templates list
  const templates = useMemo(() => {
    return templatesResult?.data?.data?.data || [];
  }, [templatesResult]);

  // Group templates by packet
  const packetsMap = useMemo(() => {
    const map = new Map<string | null, DestinationPacketDocument[]>();
    templates.forEach((template: DestinationPacketDocument) => {
      const packetId = template.destination_template_packet_id;
      if (!map.has(packetId)) {
        map.set(packetId, []);
      }
      map.get(packetId)!.push(template);
    });
    return map;
  }, [templates]);

  // Get packet options
  const packetOptions = useMemo(() => {
    const options: Array<{ value: string | null; label: string }> = [];
    packetsMap.forEach((docs, packetId) => {
      const packetName = docs[0]?.DestinationTemplatePacket?.name || "Unnamed Packet";
      options.push({
        value: packetId,
        label: packetName,
      });
    });
    return options;
  }, [packetsMap]);

  // Get documents for selected packet
  const selectedPacketDocuments = useMemo(() => {
    if (!formData.packet_id) return [];
    return packetsMap.get(formData.packet_id) || [];
  }, [formData.packet_id, packetsMap]);

  const customers: Customer[] = customersResult?.data?.data ?? [];

  // Get customer options
  const customerOptions = useMemo(() => {
    return customers.map((customer) => ({
      value: customer.id,
      label: customer.long_name,
    }));
  }, [customers]);

  const getOverrideFieldsForCustomer = (
    customerId: string | null | undefined
  ): DocumentOverrideFields | undefined => {
    if (!customerId) {
      return undefined;
    }

    const selectedCustomer = customers.find((customerItem) => customerItem.id === customerId);

    if (!selectedCustomer) {
      return undefined;
    }

    return {
      customer_name: selectedCustomer.long_name || undefined,
      customer_email: selectedCustomer.email || undefined,
    };
  };

  const handleCustomerChange = (value: string) => {
    const normalizedValue = value || "";
    const overrideFields = getOverrideFieldsForCustomer(normalizedValue);

    setFormData((prev) => ({
      ...prev,
      customer_id: normalizedValue,
      selected_documents: overrideFields
        ? prev.selected_documents.map((doc) => ({
            ...doc,
            override_fields: overrideFields,
          }))
        : prev.selected_documents,
    }));
  };

  const handlePacketChange = (value: string) => {
    const normalizedPacketId = value || null;
    const packetDocs = normalizedPacketId ? packetsMap.get(normalizedPacketId) || [] : [];

    setFormData((prev) => {
      if (!normalizedPacketId) {
        return {
          ...prev,
          packet_id: null,
          selected_documents: [],
        };
      }

      const firstDoc = packetDocs[0];
      const overrideFields = getOverrideFieldsForCustomer(prev.customer_id);

      return {
        ...prev,
        packet_id: normalizedPacketId,
        selected_documents: firstDoc
          ? [
              {
                id: firstDoc.id,
                override_fields: overrideFields || {},
              },
            ]
          : [],
      };
    });

    hasAutoSelectedRef.current = packetDocs.length > 0 ? normalizedPacketId : null;
  };

  // Auto-select first document when packet changes (only on initial packet selection)
  useEffect(() => {
    if (
      formData.packet_id &&
      selectedPacketDocuments.length > 0 &&
      formData.selected_documents.length === 0 &&
      hasAutoSelectedRef.current !== formData.packet_id
    ) {
      const firstDoc = selectedPacketDocuments[0];
      setFormData((prev) => {
        const overrideFields = getOverrideFieldsForCustomer(prev.customer_id);
        return {
          ...prev,
          selected_documents: [
            {
              id: firstDoc.id,
              override_fields: overrideFields || {},
            },
          ],
        };
      });
      hasAutoSelectedRef.current = formData.packet_id;
    }
    // Reset the ref when packet changes
    if (hasAutoSelectedRef.current !== formData.packet_id) {
      hasAutoSelectedRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.packet_id, selectedPacketDocuments.length]);

  // Validate email fields
  const validateEmailFields = (): boolean => {
    const errors: typeof fieldErrors = {};
    let isValid = true;

    formData.selected_documents.forEach((doc) => {
      if (doc.override_fields?.customer_email) {
        const emailSchema = z.string().email();
        const emailResult = emailSchema.safeParse(doc.override_fields.customer_email);
        if (!emailResult.success) {
          errors[doc.id] = {
            customer_email: "Please enter a valid email address",
          };
          isValid = false;
        }
      }
    });

    setFieldErrors(errors);
    return isValid;
  };

  const handleDocumentToggle = (documentId: string, checked: boolean) => {
    if (checked) {
      setFormData((prev) => {
        const overrideFields = getOverrideFieldsForCustomer(prev.customer_id);
        return {
          ...prev,
          selected_documents: [
            ...prev.selected_documents,
            {
              id: documentId,
              override_fields: overrideFields || {},
            },
          ],
        };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        selected_documents: prev.selected_documents.filter((doc) => doc.id !== documentId),
      }));
      // Clear errors for removed document
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[documentId];
        return newErrors;
      });
    }
  };

  const handleOverrideChange = (
    documentId: string,
    field: "customer_name" | "customer_email",
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      selected_documents: prev.selected_documents.map((doc) => {
        if (doc.id === documentId) {
          return {
            ...doc,
            override_fields: {
              ...doc.override_fields,
              [field]: value || undefined,
            },
          };
        }
        return doc;
      }),
    }));

    // Clear error when user starts typing
    if (field === "customer_email" && fieldErrors[documentId]?.customer_email) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[documentId]) {
          delete newErrors[documentId].customer_email;
          if (Object.keys(newErrors[documentId]).length === 0) {
            delete newErrors[documentId];
          }
        }
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!formData.customer_id) {
      toast({
        title: "Error",
        description: "Customer is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.packet_id) {
      toast({
        title: "Error",
        description: "Packet is required",
        variant: "destructive",
      });
      return;
    }

    if (formData.selected_documents.length === 0) {
      toast({
        title: "Error",
        description: "At least one document must be selected",
        variant: "destructive",
      });
      return;
    }

    // Validate email fields
    if (!validateEmailFields()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the document override fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Find the first selected document to get the packet document ID
      const firstSelectedDoc = selectedPacketDocuments.find(
        (doc) => doc.id === formData.selected_documents[0].id
      );

      if (!firstSelectedDoc) {
        throw new Error("Selected document not found");
      }

      const payload = {
        customer_id: formData.customer_id,
        destination_packet_document_id: firstSelectedDoc.destination_template_packet_id,
        documents: formData.selected_documents.map((doc) => ({
          id: doc.id,
          override_fields: doc.override_fields && Object.keys(doc.override_fields).length > 0
            ? doc.override_fields
            : undefined,
        })),
        is_test_send: formData.is_test_send,
        is_draft: false,
      };

      const parseResult = CreateEnvelopeInstanceValidation.safeParse(payload);

      if (!parseResult.success) {
        console.error("Failed to validate form data", parseResult.error);
        toast({
          title: "Validation Error",
          description: parseResult.error.errors.map((e) => e.message).join(", "),
          variant: "destructive",
        });
        return;
      }

      const apiResult = await createEnvelopeInstance(parseResult.data);

      if (!apiResult.success) {
        throw apiResult;
      }

      toast({
        title: "Envelope Sent",
        description: formData.is_test_send
          ? "Test envelope instance created successfully"
          : "Envelope instance created successfully",
      });

      await queryClient.invalidateQueries({ queryKey: ["destinationPackets"] });

      onSuccess?.();
    } catch (error) {
      console.error("Failed to create envelope instance", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create envelope instance";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="is_test_send"
            checked={formData.is_test_send}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({ ...prev, is_test_send: checked === true }))
            }
          />
          <Label htmlFor="is_test_send" className="cursor-pointer">
            Send as test (will not send to customer)
          </Label>
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting || formData.selected_documents.length === 0}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Envelope
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 grid">
            <Label htmlFor="customer_id">
              Customer <span className="text-destructive">*</span>
            </Label>
            <Combobox
              className="min-w-[400px]"
              popoverClassName="min-w-[400px]"
              virtualizeList
              dwimOnChange
              autofocus
              options={customerOptions}
              value={formData.customer_id}
              onValueChange={handleCustomerChange}
              placeholder="Select customer..."
              disabled={customersResult.isLoading}
              isModal={isModal}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="packet_id">
              Packet <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.packet_id || ""}
              onValueChange={handlePacketChange}
              disabled={templatesResult.isLoading || packetOptions.length === 0}
            >
              <SelectTrigger id="packet_id">
                <SelectValue
                  placeholder={
                    templatesResult.isLoading ? "Loading packets..." : "Select packet..."
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {packetOptions.map((option) => (
                  <SelectItem key={option.value || "null"} value={option.value || ""}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {formData.packet_id && selectedPacketDocuments.length > 0 && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-4">Select Documents</h3>
            <div className="space-y-4">
              {selectedPacketDocuments.map((doc) => {
                const isSelected = formData.selected_documents.some((d) => d.id === doc.id);
                const selectedDoc = formData.selected_documents.find((d) => d.id === doc.id);
                const docErrors = fieldErrors[doc.id] || {};

                return (
                  <div key={doc.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`doc-${doc.id}`}
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          handleDocumentToggle(doc.id, checked === true)
                        }
                      />
                      <Label
                        htmlFor={`doc-${doc.id}`}
                        className="cursor-pointer font-medium flex-1"
                      >
                        {doc.DocumentTemplate?.title ||
                          (doc.document_variant
                            ? doc.document_variant
                                .split("_")
                                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                                .join(" ")
                            : "Untitled Document")}
                      </Label>
                    </div>

                    {isSelected && (
                      <div className="ml-6 space-y-3 border-l-2 pl-4">
                        <p className="text-sm text-muted-foreground">
                          Override fields (optional):
                        </p>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor={`doc-${doc.id}-name`}>Customer Name</Label>
                            <Input
                              id={`doc-${doc.id}-name`}
                              value={selectedDoc?.override_fields?.customer_name || ""}
                              onChange={(e) =>
                                handleOverrideChange(doc.id, "customer_name", e.target.value)
                              }
                              placeholder="Override customer name..."
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor={`doc-${doc.id}-email`}>Customer Email</Label>
                            <Input
                              id={`doc-${doc.id}-email`}
                              type="email"
                              value={selectedDoc?.override_fields?.customer_email || ""}
                              onChange={(e) =>
                                handleOverrideChange(doc.id, "customer_email", e.target.value)
                              }
                              placeholder="Override customer email..."
                              className={docErrors.customer_email ? "border-destructive" : ""}
                            />
                            {docErrors.customer_email && (
                              <p className="text-sm text-destructive">
                                {docErrors.customer_email}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </form>
  );
}

